import React, { useEffect, useMemo, useRef, useState, forwardRef, useImperativeHandle } from "react";
import { ScrollView, View, StyleSheet } from "react-native";
import { ClientType } from "@/types/db/clients";
import useToast from "@/hooks/useToast";
import { ExpenseForm, ExpensePayload } from "./types";
import { ExpenseStatusType } from "@/types/db/expenses";
import { ExpenseFields } from "./ExpenseFields";
import { FixedAttachment } from "@/components/Attachment/FixedAttachment";
import { AttachmentFile } from "@/components/Attachment/types";
import { ThemedText } from "@/components/ThemedText";

export { ExpensePayload } from "./types";
export type { ExpenseForm } from "./types";

// Status options for dropdown
const STATUS_OPTIONS = [
  { text: "PENDING", color: "pending" },
  { text: "APPROVED", color: "complete" },
  { text: "REJECTED", color: "attention" },
  { text: "PAID", color: "todo" },
  { text: "REIMBURSED", color: "neutral" },
] as const;

type theme = ReturnType<typeof import("@/hooks/useTheme").useTheme>["colors"];

export function getExpenseStatusColor(status: string, colors: theme): string {
  const mapping: Record<string, string> = {
    PENDING: colors.pending.border,
    APPROVED: colors.complete.border,
    REJECTED: colors.attention.border,
    PAID: colors.todo.border,
    REIMBURSED: colors.neutral.border,
  };
  return mapping[status.toUpperCase()] || colors.neutral.border;
}

export const EXPENSE_STATUS_OPTIONS = STATUS_OPTIONS;

export interface ExpenseCreateFormProps {
  initialExpense?: Partial<ExpenseForm>;
  onSave?: (payload: { expense: ExpenseForm }) => Promise<void> | void;
  onChange?: (payload: ExpensePayload) => void;
  onDirtyChange?: (dirty: boolean) => void;
  onClientChange?: (client: ClientType | null) => void;
}

export interface ExpenseCreateFormRef {
  handleSave: () => Promise<void>;
  handleReset: () => void;
  updateExpenseName: (name: string) => void;
  updateStatus: (status: string) => void;
  openClientModal: () => void;
  getPendingFiles: () => AttachmentFile[];
}

export const ExpenseCreateForm = forwardRef<ExpenseCreateFormRef, ExpenseCreateFormProps>(
  ({ initialExpense, onSave, onChange, onDirtyChange, onClientChange }, ref) => {
    const toast = useToast();
    const [attachmentFiles, setAttachmentFiles] = useState<AttachmentFile[]>([]);

    // ---------- form state ----------
    const [expense, setExpense] = useState<ExpenseForm>({
      expense_number: initialExpense?.expense_number ?? "",
      description: initialExpense?.description ?? "",
      amount: initialExpense?.amount ?? 0,
      category: initialExpense?.category ?? "",
      client_id: initialExpense?.client_id ?? "",
      project_id: initialExpense?.project_id ?? "",
      status: initialExpense?.status ?? "PENDING",
      meta_data: {
        ...initialExpense?.meta_data,
        expense_number: initialExpense?.expense_number,
        description: initialExpense?.description,
        amount: initialExpense?.amount,
        category: initialExpense?.category,
        user_id: initialExpense?.meta_data?.user_id,
      },
    });

    useEffect(() => {
      onChange?.({ expense });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [expense]);

    const baseline = useRef(JSON.stringify({ expense }));
    const dirty = useMemo(() => JSON.stringify({ expense }) !== baseline.current, [expense]);

    // Notify parent of dirty state changes
    useEffect(() => {
      onDirtyChange?.(dirty);
    }, [dirty, onDirtyChange]);

    // Expose methods to parent via ref
    useImperativeHandle(ref, () => ({
      handleSave,
      handleReset,
      updateExpenseName: (name: string) => {
        setExpense((e) => ({ ...e, expense_number: name }));
      },
      updateStatus: (status: string) => {
        setExpense((e) => ({ ...e, status: status as ExpenseStatusType }));
      },
      openClientModal: () => {
        // Client selection is now inline via ClientInput
      },
      getPendingFiles: () => attachmentFiles,
    }));

    // ---------- helpers ----------
    const updateExpense = (patch: Partial<ExpenseForm>) => setExpense((e) => ({ ...e, ...patch }));

    const handleSave = async () => {
      // Validations
      if (!expense.client_id?.trim()) {
        toast.showToast({
          title: "Missing Client",
          message: "Please select a client for this expense",
          toastType: "warning",
        });
        return;
      }
      if (!expense.project_id?.trim()) {
        toast.showToast({
          title: "Missing Project",
          message: "Please select a project for this expense",
          toastType: "warning",
        });
        return;
      }
      if (!expense.status) {
        toast.showToast({
          title: "Missing Status",
          message: "Please select a status for this expense",
          toastType: "warning",
        });
        return;
      }

      try {
        await onSave?.({ expense });
        baseline.current = JSON.stringify({ expense });
      } catch (e: any) {
        throw e; // Re-throw to let parent handle it
      }
    };

    const handleReset = () => {
      const nextExpense: ExpenseForm = {
        expense_number: initialExpense?.expense_number ?? "",
        description: initialExpense?.description ?? "",
        amount: initialExpense?.amount ?? 0,
        category: initialExpense?.category ?? "",
        client_id: initialExpense?.client_id ?? "",
        project_id: initialExpense?.project_id ?? "",
        status: initialExpense?.status ?? "PENDING",
        meta_data: {
          ...initialExpense?.meta_data,
          expense_number: initialExpense?.expense_number,
          description: initialExpense?.description,
          amount: initialExpense?.amount,
          category: initialExpense?.category,
          user_id: initialExpense?.user_id ?? "",
        },
      };
      setExpense(nextExpense);
      setAttachmentFiles([]);
      onClientChange?.(null);
      baseline.current = JSON.stringify({ expense: nextExpense });
    };

    return (
      <View style={styles.container}>
        <ScrollView style={{ flex: 1 }} contentContainerStyle={{ minHeight: "100%", gap: 16 }}>
          <ExpenseFields expense={expense} updateExpense={updateExpense} />

          {/* Attachments Section */}
          <View style={{ gap: 8 }}>
            <ThemedText type="defaultSemiBold" style={{ fontSize: 16 }}>
              Attachments
            </ThemedText>
            <ThemedText style={{ fontSize: 12, opacity: 0.7 }}>
              Files will be uploaded when you save the expense
            </ThemedText>
            <FixedAttachment
              files={attachmentFiles}
              onFilesChange={setAttachmentFiles}
              autoUpload={false}
              maxFiles={10}
              maxFileSize={50 * 1024 * 1024}
              mode="grid"
              showPreviews={true}
              dragDropEnabled={true}
              showUploadButton={true}
              placeholder="Add files to this expense"
              testID="expense-attachment"
            />
          </View>
        </ScrollView>
      </View>
    );
  },
);

ExpenseCreateForm.displayName = "ExpenseCreateForm";

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});
